import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from '../../assets';
import { ListPageTemplate } from '../../components/page-templates/list-page/ListPageTemplate';
import type { TableColumn } from '../../components/shared/tables/TableSection';
import { UserContext } from '../../components/shared/useUser';
import type {
  NewsDisplayStatus,
  NewsManagementListItem,
} from '../../app/services/api/news/newsManagementTypes';
import { useNewsManagementClient } from '../../hooks/api/useNewsManagementClient';

const PAGE_SIZE = 25;

const statusColor = (status: NewsDisplayStatus): 'default' | 'primary' | 'success' | 'warning' =>
  status === 'Published'
    ? 'success'
    : status === 'Scheduled'
      ? 'primary'
      : status === 'Draft'
        ? 'warning'
        : 'default';

const NewsManagementPage = () => {
  const navigate = useNavigate();
  const client = useNewsManagementClient();
  const { hasPermissionTo, selectedFranchise } = useContext(UserContext);

  const canCreate = hasPermissionTo(['NewsDraftWrite']);
  const canEdit = hasPermissionTo(['NewsDraftWrite']);
  const canPublish = hasPermissionTo(['NewsPublish']);
  const canArchive = hasPermissionTo(['NewsArchive']);

  const [items, setItems] = useState<NewsManagementListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<NewsDisplayStatus | ''>('');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);

  const tenantId = selectedFranchise?.tenantId;
  const franchiseId = selectedFranchise?.franchiseId;

  const load = useCallback(async () => {
    if (!tenantId) {
      setItems([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await client.getItems({
        tenantId,
        franchiseId,
        status: status || undefined,
        isPinned: pinnedOnly ? true : undefined,
        search: search.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
    } finally {
      setLoading(false);
    }
  }, [client, franchiseId, page, pinnedOnly, search, status, tenantId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      load().catch(() => {
        setItems([]);
        setTotalCount(0);
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [search, status, pinnedOnly, franchiseId]);

  const refreshItem = useCallback(async (id: number) => {
    const updated = await client.getItem(id);
    setItems(current => current.map(item => item.id === id ? {
      ...item,
      revisionNumber: updated.revisionNumber,
      title: updated.title,
      summary: updated.summary,
      status: updated.status,
      isPinned: updated.isPinned,
      commentsEnabled: updated.commentsEnabled,
      reactionsEnabled: updated.reactionsEnabled,
      visibleFromUtc: updated.visibleFromUtc,
      visibleUntilUtc: updated.visibleUntilUtc,
      updatedAtUtc: updated.updatedAtUtc,
      publishedAtUtc: updated.publishedAtUtc,
      rowVersion: updated.rowVersion,
    } : item));
  }, [client]);

  const publish = useCallback(async (item: NewsManagementListItem) => {
    setActionId(item.id);
    try {
      await client.publish(item.id, item.rowVersion, item.visibleFromUtc, item.visibleUntilUtc);
      await refreshItem(item.id);
    } finally {
      setActionId(null);
    }
  }, [client, refreshItem]);

  const archive = useCallback(async (item: NewsManagementListItem) => {
    setActionId(item.id);
    try {
      await client.archive(item.id, item.rowVersion);
      await refreshItem(item.id);
    } finally {
      setActionId(null);
    }
  }, [client, refreshItem]);

  const columns = useMemo<readonly TableColumn<NewsManagementListItem>[]>(() => [
    {
      id: 'title',
      label: 'Title',
      render: item => item.title,
    },
    {
      id: 'status',
      label: 'Status',
      render: item => (
        <Chip
          label={item.status}
          size="small"
          color={statusColor(item.status)}
          variant="outlined"
        />
      ),
    },
    {
      id: 'revision',
      label: 'Revision',
      render: item => item.revisionNumber,
    },
    {
      id: 'pinned',
      label: 'Featured',
      render: item => item.isPinned ? <PushPinIcon fontSize="small" /> : null,
    },
    {
      id: 'engagement',
      label: 'Engagement',
      render: item => [
        item.commentsEnabled ? 'Comments' : null,
        item.reactionsEnabled ? 'Likes' : null,
      ].filter(Boolean).join(' + ') || 'Off',
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: item => (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
        >
          {canPublish && item.status === 'Draft' && (
            <Tooltip title="Publish">
              <span>
                <IconButton
                  size="small"
                  disabled={actionId === item.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    publish(item).catch(() => undefined);
                  }}
                >
                  <PublishIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canArchive && (item.status === 'Published' || item.status === 'Scheduled') && (
            <Tooltip title="Archive">
              <span>
                <IconButton
                  size="small"
                  disabled={actionId === item.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    archive(item).catch(() => undefined);
                  }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/news-management/${item.id}`);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ], [actionId, archive, canArchive, canEdit, canPublish, navigate, publish]);

  return (
    <ListPageTemplate
      title="News & Communications"
      subtitle="Create, publish and manage operational communications."
      actions={canCreate ? (
        <Button
          size="lg"
          variant="primary"
          startIcon={<Plus />}
          onClick={() => navigate('/news-management/new')}
        >
          Create News Item
        </Button>
      ) : undefined}
      filterContent={(
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
        >
          <TextField
            label="Status"
            select
            size="small"
            value={status}
            onChange={event => setStatus(event.target.value as NewsDisplayStatus | '')}
            sx={{ minWidth: 180, mb: 0 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Scheduled">Scheduled</MenuItem>
            <MenuItem value="Published">Published</MenuItem>
            <MenuItem value="Expired">Expired</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
          </TextField>
          <TextField
            label="Featured"
            select
            size="small"
            value={pinnedOnly ? 'yes' : 'all'}
            onChange={event => setPinnedOnly(event.target.value === 'yes')}
            sx={{ minWidth: 180, mb: 0 }}
          >
            <MenuItem value="all">All items</MenuItem>
            <MenuItem value="yes">Featured only</MenuItem>
          </TextField>
        </Stack>
      )}
      table={{
        title: 'News Items',
        columns,
        rows: items,
        getRowId: item => item.id,
        onRowClick: item => navigate(`/news-management/${item.id}`),
        recordCount: totalCount,
        recordCountLabel: 'items',
        searchValue: search,
        searchPlaceholder: 'Search title or summary',
        onSearchChange: setSearch,
        page,
        maxPages: Math.max(1, Math.ceil(totalCount / PAGE_SIZE)),
        onPageChange: setPage,
        loading,
        emptyMessage: 'No news items match these filters.',
      }}
    />
  );
};

export default NewsManagementPage;
